// Imports ------------------------------------------------------------------->

import {Component} from "../Component";
import {element} from "../CustomElement";
import {i18n} from "../../i18n/I18n";
import {icons} from "../../icons/Icons";

import {Dialog} from "../flipp-dialog/Dialog";
import {LoginButton} from "../flipp-login-button/LoginButton";
import {I18nLink} from "../flipp-i18n-link/I18nLink";

import {inject} from "../../services/di";
import {AnalyticsService} from "../../services/AnalyticsService";
import {EventService} from "../../services/EventService";
import {ShoppingListService} from "../../services/ShoppingListService";
import {AuthStatus} from "../../services/definitions/IAuthenticationService";
import {AuthenticationService} from "../../services/AuthenticationService";
import {ConfigService} from "../../services/ConfigService";
import {AuthProvider} from "../../definitions/CommonDefinitions";

// Private Definitions ------------------------------------------------------->

// Public Definitions -------------------------------------------------------->

@element("flipp-account-trigger-element")
export class AccountTriggerElement extends Component<{}, void> {
	@inject auth: AuthenticationService;
	@inject eventService: EventService;
	@inject analytics: AnalyticsService;
	@inject config: ConfigService;

	@inject shoppingList: ShoppingListService;

	refs: {
		loginDialog: Dialog,
		logoutDialog: Dialog,
		loginButton: HTMLButtonElement,
		closeBtn: HTMLButtonElement,
		formSignInHeader: HTMLElement,
		formSignInText: HTMLElement,
		flippLogo: HTMLDivElement
	};

	prepare() {
		window.addEventListener("show-modal-form", this.showAuthDialogForm);
	}

	onAttach() {
		window.addEventListener("flipp-auth", this.handleAuthEvent_);
	}

	onDetach() {
		window.removeEventListener("flipp-auth", this.handleAuthEvent_);
		window.removeEventListener("show-modal-form", this.showAuthDialogForm);
	}

	render() {

		const links: {[key: number]: string} = {
			1: "https://corp.flipp.com/terms",
			2: "https://corp.flipp.com/privacy"
		};

		const defaultProfile = `https://f.wishabi.net/creative/Distribution/images/profile.png`;

		return <div flex-vertical>
			<button class="authenticate"
							ref="loginButton"
							onClick={this.handleSignInBtn}
							anchor-point="right bottom">
				{this.auth.status === AuthStatus.LOGGED_IN ?
					<div flex-horiztonal>
						<img src={this.auth.user.info.image || defaultProfile} onError={`this.src='${defaultProfile}'`} alt="" />
						<span>{this.auth.user.info.name || this.auth.user.info.email}</span>
					</div>
				:
					<div flex-horiztonal>
						<img src={defaultProfile} alt="" />
						<span>{i18n("signin")}</span>
					</div>
				}
			</button>
			<Dialog class="login-dialog" ref="loginDialog">
				<div class="content-wrapper" flex-vertical>
					<div class="logo" flex-horizontal>
						<h2 ref="formSignInHeader">{i18n("signin_title")}</h2>
						<div ref="flippLogo">{icons.flippLogo}</div>
					</div>
					<button onClick={this.handleDialogClose_} class="close">
						{icons.x}
					</button>
					<p ref="formSignInText">{i18n("signin_value")}</p>
					<p class="no-post">{i18n("stop_all_the_posting")}</p>
					<LoginButton provider={AuthProvider.GOOGLE} context="topbar" />
					<LoginButton provider={AuthProvider.FACEBOOK} context="topbar" />
					<p class="legal">
						<I18nLink key="signin_legal" values={JSON.stringify(links)} />
					</p>

				</div>
			</Dialog>
			<Dialog class="logout-dialog" ref="logoutDialog" anchor-point="right top">
				<div class="arrow"></div>
				{this.auth.status === AuthStatus.LOGGED_IN ?
					<div>
						{this.auth.user.info.name ?
							<div class="details" flex-horiztonal>
								<div>{i18n("name")}</div>
								<div>{this.auth.user.info.name}</div>
							</div>
						:
							null
						}
						<div class="details" flex-horiztonal>
							<div>{i18n("email")}</div>
							<div>{this.auth.user.info.email}</div>
						</div>
						<button onClick={this.handleLogout_}><span>{i18n("signout")}</span></button>
					</div>
				: null
				}
			</Dialog>
		</div>;
	}

	private showAuthDialogForm = (e: Event) => {
		this.refs.formSignInHeader.textContent = i18n("signin_title_form");
		this.refs.formSignInText.textContent = i18n("signin_value_form");
		this.refs.flippLogo.classList.add("hidden-logo");
		this.showAuthDialog_(null);
	}

	private handleSignInBtn = (e: Event) => {
		this.refs.formSignInHeader.textContent = i18n("signin_title");
		this.refs.formSignInText.textContent = i18n("signin_value");
		this.refs.flippLogo.classList.remove("hidden-logo");
		this.showAuthDialog_(null);
	}

	private showAuthDialog_ = (e: Event) => {
		if (this.auth.status === AuthStatus.LOGGED_IN) {
			this.refs.logoutDialog.showModal(this.refs.loginButton);
		} else {
			this.refs.loginDialog.showModal();
		}
	}

	private handleLogout_ = () => {
		const provider = this.auth.user.info.provider;
		const userGuid = this.auth.user.token.user_id;
		this.auth.logout(this).then(() => {
			this.analytics.setAccountGuid(null);
			this.eventService.fireAuthEvent("logout", null);
			this.eventService.redirectWhenLogoutInForm();
			this.analytics.trackSignout(userGuid, provider);
		}).catch((e: Error) => {});
		this.eventService.unsubscribeFromRedirect();
	}

	private handleAuthEvent_ = (e: CustomEvent) => {
		if (this.refs.loginDialog.open) {
			this.refs.loginDialog.close();
		}
		if (this.refs.logoutDialog.open) {
			this.refs.logoutDialog.close();
		}
		this.invalidate();
	}

	private handleDialogClose_ = (e: Event) => {
		this.refs.loginDialog.close();
		this.eventService.changeUserLocationForm();
		this.eventService.unsubscribeFromRedirect();
	}
}
